import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PageContainer from '@components/Layout/PageContainer';
import { CardsGrid } from '@components/CardItem/styles';
import PageHeader from '@features/PageHeader';
import { Typography } from '@components/Typography';
import Button from '@components/Button';
import { ModalConfirm } from '@components/Modal';
import { useTranslation, useHasAccessToAsset } from '@hooks';
import usePermissions from '@hooks/usePermissions';
import { alertActions, brandsActions, channelsActions, formsActions, micrositesActions } from '@actions';
import { micrositesService } from '@services';
import { VIEW_TYPE } from '@constants/microsites';
import { handleApiError } from '@helpers/api';
import MicrositeItem from './MicrositeItem';
import EditModal from './EditModal';
import { MicrositesIcon } from 'assets/images/icons';
import { ProductEvent } from '@constants/product-events';
import PostHogManager from '@widgets/posthog-manager';

const MicrositesPage = () => {
    const { t } = useTranslation();
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedMicrosite, setSelectedMicrosite] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [modal, setModal] = useState({ open: false, type: VIEW_TYPE.EDIT });
    const [restrictedNames, setRestrictedNames] = useState([]);

    const { hasFullAccess } = usePermissions();
    const { hasAccessToAssetById } = useHasAccessToAsset();
    const account = useSelector(state => state.account?.account);
    const data = useSelector(state => state.microsites.data);
    const brands = useSelector(state => state.brands.data);
    const dispatch = useDispatch();

    useEffect(() => {
        async function fetchData() {
            try {
                const restrictedNames = await micrositesService.getRestrictedNames();
                setRestrictedNames(restrictedNames);
            } catch (_) {
                //
            }
        }
        dispatch(micrositesActions.getAll());
        dispatch(channelsActions.get());
        dispatch(brandsActions.getAll());
        dispatch(formsActions.getAll());
        fetchData();
    }, []);

    const onCreateEmptyMicrosite = async () => {
        setIsSubmitting(true);
        try {
            const newMicrosite = await micrositesService.createEmptyMicrosite();
            await dispatch(micrositesActions.getAll());
            setSelectedMicrosite({ ...newMicrosite, restrictedNames });
            const rn = await micrositesService.getRestrictedNames();
            setRestrictedNames(rn);
            setModal({ open: true, type: VIEW_TYPE.CREATE });
        } catch (error) {
            dispatch(alertActions.error(t('alertMessages.createFail')));
        } finally {
            setIsSubmitting(false);
        }
    };

    const onDelete = async () => {
        try {
            await micrositesService.deleteMicrosite(selectedMicrosite.id);
            await dispatch(micrositesActions.getAll());
            const rn = await micrositesService.getRestrictedNames();
            setRestrictedNames(rn);
            dispatch(alertActions.success(t('alertMessages.deleteSuccess')));
            setSelectedMicrosite(null);
            if (modal.open) {
                setModal({ open: false, type: VIEW_TYPE.EDIT });
            }
            setDeleteModalOpen(false);
        } catch (error) {
            dispatch(alertActions.error(t('alertMessages.deleteFail')));
        }
    };

    const onCloseModal = () => {
        setModal({ open: false, type: VIEW_TYPE.EDIT });
        setSelectedMicrosite(null);
    };

    const onCancelCreate = async () => {
        if (!selectedMicrosite?.id) {
            onCloseModal();
            return;
        }
        try {
            await micrositesService.deleteMicrosite(selectedMicrosite.id);
            await dispatch(micrositesActions.getAll());
            const rn = await micrositesService.getRestrictedNames();
            setRestrictedNames(rn);
        } catch (error) {
            dispatch(alertActions.error(t('alertMessages.deleteFail')));
        } finally {
            onCloseModal();
        }
    };

    const onSave = async values => {
        try {
            await micrositesService.updateMicrosite({ ...selectedMicrosite, ...values });
            if (modal.type === VIEW_TYPE.CREATE) {
                PostHogManager.capture(ProductEvent.MicrositeCreated);
            }
            await dispatch(micrositesActions.getAll());
            const rn = await micrositesService.getRestrictedNames();
            setRestrictedNames(rn);
            dispatch(alertActions.success(t('alertMessages.editSuccess')));
            setSelectedMicrosite(null);
            setModal({ open: false, type: VIEW_TYPE.EDIT });
        } catch (error) {
            handleApiError(error, 'apiErrors.editFail');
        }
    };

    return (
        <PageContainer>
            <PageHeader
                styles={{ marginBottom: '16px' }}
                icon={<MicrositesIcon />}
                title={t('MicrositesPage.createCustomLending')}
                subtitle={t('MicrositesPage.createCustomLendingSubTitle')}
                metrics={
                    <Typography variant="body1" sx={{ color: 'var(--text-secondary)' }}>
                        {t('MicrositesPage.created')} {data?.totalCount}
                    </Typography>
                }
            >
                {hasFullAccess('microsites') && (
                    <Button variant="contained" onClick={onCreateEmptyMicrosite} disabled={isSubmitting}>
                        {t('buttons.createNewMicrosite')}
                    </Button>
                )}
            </PageHeader>
            <CardsGrid>
                {data?.microsites.map(item => (
                    <MicrositeItem
                        key={item.id}
                        microsite={item}
                        canWrite={hasAccessToAssetById(item, 'microsites').w}
                        isSubmitting={isSubmitting}
                        onClickEdit={() => {
                            setSelectedMicrosite({
                                ...item,
                                restrictedNames: restrictedNames.filter(n => n !== item.slug),
                            });
                            setModal({ open: true, type: VIEW_TYPE.EDIT });
                        }}
                        onClickView={() => {
                            setSelectedMicrosite(item);
                            setModal({ open: true, type: VIEW_TYPE.VIEW });
                        }}
                        onClickDelete={() => {
                            setSelectedMicrosite(item);
                            setDeleteModalOpen(true);
                        }}
                    />
                ))}
            </CardsGrid>
            {modal.open ? (
                <EditModal
                    modal={modal}
                    account={account}
                    onClose={onCloseModal}
                    onCancel={onCancelCreate}
                    selectedMicrosite={selectedMicrosite}
                    onDelete={() => setDeleteModalOpen(true)}
                    onSave={onSave}
                    isSubmitting={isSubmitting}
                    isUser={!hasAccessToAssetById(selectedMicrosite, 'microsites').w}
                    brands={brands}
                />
            ) : null}
            {isDeleteModalOpen && (
                <ModalConfirm
                    isOpen={isDeleteModalOpen}
                    handleClose={() => setDeleteModalOpen(false)}
                    title={t('common.deleteTitle')}
                    description={t('common.deleteDescription')}
                    onPrimaryAction={onDelete}
                    primaryActionText={t('buttons.deleteSite')}
                    primaryActionType="danger"
                />
            )}
        </PageContainer>
    );
};

export default MicrositesPage;
